"use client";
import { useEffect, useEffectEvent, useRef, useState } from "react";

import { TRANSACTABLE_STATES } from "~/context/TransactableContext";
import { useTransactable } from "~/hooks/use-transactable";
import {
  AppModalKey,
  useAppModal,
  useProfileModal,
  useVerifyEmailModal,
} from "~/modals";

/**
 * Opens the app-wide modal for the transactable step the guest is on, and closes the
 * others. Each modal hands control back through `next` once its step is done; a
 * dismissal stops the flow. Call it below `TransactableProvider`. Its modals are
 * global, and the auth steps open ProfileModal and VerifyEmailModal too, so it closes
 * only the ones it opened.
 */
const useTransactableModals = () => {
  const {
    readyToTransact,
    totalPendingReviews,
    currentPendingReview,
    pendingReviewsIndex,
    transactableState,
    next,
    setTransactionProcessing,
    transactionProcessing,
    profileData,
  } = useTransactable();
  const pendingReviewsModal = useAppModal(AppModalKey.PendingReviewsModal);
  const profileModal = useProfileModal();
  const verifyEmailModal = useVerifyEmailModal();

  const [emailVerificationCodeSent, setEmailVerificationCodeSent] =
    useState(false);

  // The step the modals were last synced to, so a shared modal is closed only when
  // this flow is the one leaving it.
  const syncedStepRef = useRef<string | null>(null);

  const stopTransaction = () => setTransactionProcessing(false);

  // An effect event, so it opens the modals with the latest callbacks without the
  // effect re-running on them; the modal hooks hand out new functions every render.
  const syncModals = useEffectEvent(() => {
    const step =
      !readyToTransact && transactionProcessing ? transactableState : null;
    const previousStep = syncedStepRef.current;
    syncedStepRef.current = step;

    if (step === TRANSACTABLE_STATES.PENDING_REVIEWS && currentPendingReview) {
      pendingReviewsModal.openModal({
        onClose: stopTransaction,
        onSubmit: next,
        pendingReview: currentPendingReview,
        reviewNumber: pendingReviewsIndex + 1,
        totalReviews: totalPendingReviews,
      });
    } else if (previousStep === TRANSACTABLE_STATES.PENDING_REVIEWS) {
      pendingReviewsModal.closeModal();
    }

    if (step === TRANSACTABLE_STATES.MISSING_PROFILE_DATA) {
      profileModal.openModal({
        defaultFormValues: {
          firstName: profileData?.first_name,
          lastName: profileData?.last_name,
          phoneNumber: profileData?.phone_formatted,
          companyName: profileData?.company_name,
          jobTitle: profileData?.job_title,
          avatarUrl: profileData?.avatar_url,
          referralSource: profileData?.referral_source,
        },
        onClose: stopTransaction,
        onSuccess: next,
        validateFormOnMount: true,
      });
    } else if (previousStep === TRANSACTABLE_STATES.MISSING_PROFILE_DATA) {
      profileModal.closeModal();
    }

    if (step === TRANSACTABLE_STATES.EMAIL_VERIFICATION) {
      verifyEmailModal.openModal({
        onClose: stopTransaction,
        onCodeSent: () => setEmailVerificationCodeSent(true),
        onSuccess: next,
        sendCodeOnMount: !emailVerificationCodeSent,
      });
    } else if (previousStep === TRANSACTABLE_STATES.EMAIL_VERIFICATION) {
      verifyEmailModal.closeModal();
    }
  });

  useEffect(() => {
    syncModals();
  }, [
    transactableState,
    readyToTransact,
    transactionProcessing,
    currentPendingReview,
    profileData,
  ]);
};

export { useTransactableModals };
